'use client';

import React, { useState, useEffect } from 'react';
import { DynamicCombobox } from '../../DynamicCombobox';
import { DatePicker } from '../../date-picker';
import { ProductCombobox } from '../../ProductCombobox';
import { useProductSearch } from '@shared/hooks/useProductSearch';
import type { SmartComboboxProps } from '../types';
import Combobox from './Combobox';
import DateRangePicker from './DateRangePicker';
import TextInput from './TextInput';
import NumberInput from './NumberInput';

/**
 * 🎯 SmartCombobox - Componente inteligente que decide qué tipo de input usar
 *
 * Basado en el tipo de columna y configuración, renderiza:
 * - 📅 DatePicker para fechas individuales
 * - 📅 DateRangePicker para rangos de fechas
 * - 📝 TextInput para texto libre
 * - 🔍 DynamicCombobox para búsquedas dinámicas
 * - 🛒 ProductCombobox para productos (con imagen, nombre y código)
 * - 📋 Combobox estático para opciones limitadas
 * - 🚀 Combobox con carga dinámica usando loadValues
 */
const SmartCombobox = React.memo(
  ({
    filterOption,
    value,
    setValue,
    placeholder,
    disabled = false,
    dataTestId,
    translations,
  }: SmartComboboxProps) => {
    const { column, values, loadValues } = filterOption;
    const columnType = column.type;
    const [dynamicOptions, setDynamicOptions] = useState<
      { label: string; value: string }[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // 🛒 Hook de búsqueda de productos (solo se usa si values === 'product')
    const { searchProductsForCombobox } = useProductSearch();

    // 🎯 Cargar opciones dinámicamente cuando se selecciona una columna con loadValues
    useEffect(() => {
      if (loadValues && column.value && columnType === 'select') {
        setIsLoading(true);
        loadValues(column.value)
          .then((options) => {
            setDynamicOptions(options);
          })
          .catch((error) => {
            console.error('Error loading values:', error);
            setDynamicOptions([]);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }, [loadValues, column.value, columnType]);

    // 🎯 Función helper para parsear fechas en formato 'YYYY-MM-DD' a zona horaria local
    // Helper function to parse dates in 'YYYY-MM-DD' format to local timezone
    const parseLocalDate = (dateString: string): Date => {
      // Parsear el string 'YYYY-MM-DD' y crear fecha en zona horaria local
      // Parse 'YYYY-MM-DD' string and create date in local timezone
      const [year, month, day] = dateString.split('-').map(Number);
      // month - 1 porque Date usa meses 0-indexados (0 = enero, 11 = diciembre)
      // month - 1 because Date uses 0-indexed months (0 = January, 11 = December)
      return new Date(year, month - 1, day);
    };

    // 🎯 Determinar qué componente usar basado en el tipo de columna
    switch (columnType) {
      case 'date': {
        // 📅 Usar DatePicker para fechas individuales
        return (
          <div className="min-w-0">
            <DatePicker
              date={value ? parseLocalDate(value) : undefined}
              onDateChange={(date) =>
                setValue(date ? date.toLocaleDateString('en-CA') : '')
              }
              placeholder={translations?.selectDate || placeholder}
              disabled={disabled}
              data-testid={dataTestId}
              className="h-10 truncate whitespace-nowrap overflow-hidden"
            />
          </div>
        );
      }

      case 'date-range': {
        // 📅 Usar DateRangePicker para rangos de fechas
        return (
          <div className="min-w-0">
            <DateRangePicker
              filterOption={filterOption}
              value={value}
              setValue={setValue}
              placeholder={translations?.selectDateRange || placeholder}
              disabled={disabled}
              dataTestId={dataTestId}
              translations={{
                from: translations?.from,
                to: translations?.to,
              }}
            />
          </div>
        );
      }

      case 'number': {
        // 🔢 Usar NumberInput para campos numéricos
        return (
          <NumberInput
            filterOption={filterOption}
            value={value}
            setValue={setValue}
            placeholder={translations?.enterText || placeholder}
            disabled={disabled}
            dataTestId={dataTestId}
          />
        );
      }

      case 'text': {
        // 🎯 Para tipo 'text', determinar si es texto libre o selector
        if (values === null) {
          // 📝 Usar TextInput para texto libre cuando values es null
          return (
            <TextInput
              filterOption={filterOption}
              value={value}
              setValue={setValue}
              placeholder={translations?.enterText || placeholder}
              disabled={disabled}
              dataTestId={dataTestId}
            />
          );
        }
        // Si values no es null, usar lógica de selectores
        const isDynamicText = values === 'dynamic';

        if (isDynamicText && filterOption.searchFn) {
          return (
            <DynamicCombobox
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              searchFn={filterOption.searchFn}
              disabled={disabled}
              dataTestId={dataTestId}
              debounceMs={filterOption.dynamicConfig?.debounceMs}
              pageSize={filterOption.dynamicConfig?.pageSize}
              emptyMessage={filterOption.dynamicConfig?.emptyMessage}
              loadingMessage={filterOption.dynamicConfig?.loadingMessage}
              onOptionSelected={(_opt) => {
                // 🎯 Propagamos el label hacia el contenedor si fue provisto
                if (translations) {
                  // no-op, solo para evitar warnings si no pasan onLabelSelected
                }
                // @ts-expect-error Prop opcional agregada
                typeof (arguments as any).callee && undefined;
              }}
            />
          );
        }

        const staticTextOptions = Array.isArray(values) ? values : [];
        return (
          <Combobox
            options={staticTextOptions}
            placeholder={placeholder}
            value={value}
            setValue={setValue}
            disabled={disabled}
            dataTestId={dataTestId}
            translations={translations}
          />
        );
      }

      case 'select':
      default: {
        // 🛒 Detectar si es un filtro de productos
        const isProduct = values === 'product';
        
        if (isProduct) {
          // 🛒 Usar ProductCombobox para productos (con imagen, nombre y código)
          return (
            <ProductCombobox
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              searchFn={searchProductsForCombobox}
              disabled={disabled}
              dataTestId={dataTestId}
              debounceMs={filterOption.dynamicConfig?.debounceMs || 300}
              pageSize={filterOption.dynamicConfig?.pageSize || 100}
              emptyMessage={filterOption.dynamicConfig?.emptyMessage || translations?.notFound}
              loadingMessage={filterOption.dynamicConfig?.loadingMessage}
            />
          );
        }
        
        // 🎯 Determinar si usar DynamicCombobox o Combobox estático
        const isDynamic = values === 'dynamic';

        if (isDynamic && filterOption.searchFn) {
          // 🚀 Usar DynamicCombobox para grandes volúmenes de datos
          return (
            <DynamicCombobox
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              searchFn={filterOption.searchFn}
              disabled={disabled}
              dataTestId={dataTestId}
              debounceMs={filterOption.dynamicConfig?.debounceMs}
              pageSize={filterOption.dynamicConfig?.pageSize}
              emptyMessage={filterOption.dynamicConfig?.emptyMessage}
              loadingMessage={filterOption.dynamicConfig?.loadingMessage}
            />
          );
        }

        // 🚀 Usar opciones cargadas dinámicamente si loadValues está disponible
        if (loadValues && dynamicOptions.length > 0) {
          return (
            <Combobox
              options={dynamicOptions}
              placeholder={isLoading ? 'Cargando...' : placeholder}
              value={value}
              setValue={setValue}
              disabled={disabled || isLoading}
              dataTestId={dataTestId}
              translations={translations}
            />
          );
        }

        // 📋 Usar Combobox estático para opciones limitadas
        const staticOptions = Array.isArray(values) ? values : [];

        return (
          <Combobox
            options={staticOptions}
            placeholder={placeholder}
            value={value}
            setValue={setValue}
            disabled={disabled}
            dataTestId={dataTestId}
            translations={translations}
          />
        );
      }
    }
  }
);

SmartCombobox.displayName = 'SmartCombobox';

export default SmartCombobox;
